import { ChevronUp, ChevronDown, Mail } from "lucide-react";

/**
 * Shared expandable card used by /admin tabs that show inbox-style
 * submission queues (Contact Questions, Partner Applications, and any
 * future "review one at a time" admin list).
 *
 * Why a render-slot API instead of a fields prop?
 *   The two consumer tabs render *different fields* in the collapsed
 *   summary (name vs. name+company) and *different field sets* in the
 *   expanded body (free-text message vs. offering+provinces+referral).
 *   A fields-prop API would either be lossy or force every consumer
 *   into the same shape. Render slots keep field rendering in the
 *   consumer where it belongs, and put just the *wrapper chrome*
 *   (border, expand/collapse, header click, divider) in one place.
 *
 * Props:
 *   testId   — root data-testid for the row
 *   expanded — bool: is the row currently expanded?
 *   onToggle — () => void: caller toggles its own expanded-id state
 *   summary  — ReactNode: collapsed-view content (name, status badge, date, snippet)
 *   details  — ReactNode: expanded-view content (full message, extra fields)
 *   actions  — ReactNode: button row pinned to the bottom of the expanded body
 */
const ExpandableSubmissionCard = ({ testId, expanded, onToggle, summary, details, actions }) => (
  <div
    className="border border-[#E5DFEC] rounded-xl bg-white overflow-hidden"
    data-testid={testId}
  >
    <button
      onClick={onToggle}
      className="w-full text-left flex items-center gap-3 p-4 hover:bg-[#F0ECF7]/30 transition-colors"
    >
      <div className="flex-1 min-w-0">{summary}</div>
      {expanded
        ? <ChevronUp className="w-4 h-4 text-[#94A3B8] shrink-0" />
        : <ChevronDown className="w-4 h-4 text-[#94A3B8] shrink-0" />}
    </button>
    {expanded && (
      <div className="px-4 pb-4 border-t border-[#E5DFEC] bg-[#FAF7F2]/40">
        <div className="mt-3">{details}</div>
        {actions && (
          <div className="flex items-center gap-2 flex-wrap mt-4">{actions}</div>
        )}
      </div>
    )}
  </div>
);

/** Status pill matching the existing "new" / "replied" admin convention. */
export const SubmissionStatusBadge = ({ status }) => (
  <span
    className="px-2 py-0.5 rounded-full text-xs font-medium capitalize"
    style={{
      backgroundColor: status === "replied" ? "#10B9811A" : "#6B4E8A1A",
      color: status === "replied" ? "#10B981" : "#6B4E8A",
    }}
  >
    {status === "replied" ? "replied" : "new"}
  </span>
);

/** Delivery-status pill. emailed=true → "sent" (purple), false → "confirm failed" (red), undefined → nothing. */
export const SubmissionSentBadge = ({ emailed }) => {
  if (emailed === true) {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#F0ECF7] text-[#6B4E8A]">
        <Mail className="w-3 h-3 inline mr-0.5" /> sent
      </span>
    );
  }
  if (emailed === false) {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#FEE2E2] text-[#EF4444]">
        <Mail className="w-3 h-3 inline mr-0.5" /> confirm failed
      </span>
    );
  }
  return null;
};

export default ExpandableSubmissionCard;
